import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    FormLabel,
    FormGroup,
    FormControlLabel,
    Checkbox,
    RadioGroup,
    Radio,
    Typography,
    Box,
    Divider
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';

const PrintDialog = ({ open, onClose, onPrint, availableGroups, printScope, getFilteredGroupCount }) => {
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [selectedType, setSelectedType] = useState('both');

    // Reset selections when dialog opens
    useEffect(() => {
        if (open) {
            // Select all groups by default
            setSelectedGroups(availableGroups.map(g => g.groupUuid));
            setSelectedType('both');
        }
    }, [open, availableGroups]);

    const handleGroupToggle = (groupUuid) => {
        setSelectedGroups(prev => {
            if (prev.includes(groupUuid)) {
                return prev.filter(uuid => uuid !== groupUuid);
            } else {
                return [...prev, groupUuid];
            }
        });
    };

    const handleSelectAllGroups = () => {
        if (selectedGroups.length === availableGroups.length) {
            setSelectedGroups([]);
        } else {
            setSelectedGroups(availableGroups.map(g => g.groupUuid));
        }
    };

    const handlePrint = () => {
        if (selectedGroups.length === 0) {
            // If no groups selected, print all groups
            onPrint(availableGroups.map(g => g.groupUuid), selectedType);
        } else {
            onPrint(selectedGroups, selectedType);
        }
    };

    const getSummaryText = () => {
        // Calculate actual filtered group count after applying type filter
        const groupsToFilter = selectedGroups.length === 0 ? availableGroups.map(g => g.groupUuid) : selectedGroups;
        const actualGroupCount = getFilteredGroupCount ? getFilteredGroupCount(groupsToFilter, selectedType) : 
                                 (selectedGroups.length === 0 ? availableGroups.length : selectedGroups.length);
        const typeText = selectedType === 'both' ? 'Remote and Non-Remote' : 
                        selectedType === 'remote' ? 'Remote' : 'Non-Remote';
        return `${actualGroupCount} ${actualGroupCount === 1 ? 'group' : 'groups'} • ${typeText}`;
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {printScope === 'day' ? 'Print Day' : 'Print Week'}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, paddingTop: 1 }}>
                    {/* Groups Selection */}
                    <FormControl component="fieldset">
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1 }}>
                            <FormLabel component="legend" sx={{ fontWeight: 'bold' }}>
                                Property Groups
                            </FormLabel>
                            <Button
                                size="small"
                                onClick={handleSelectAllGroups}
                                sx={{ textTransform: 'none' }}
                            >
                                {selectedGroups.length === availableGroups.length ? 'Deselect All' : 'Select All'}
                            </Button>
                        </Box>
                        <FormGroup>
                            {availableGroups.length === 0 ? (
                                <Typography sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                    No groups available
                                </Typography>
                            ) : (
                                availableGroups.map((group) => (
                                    <FormControlLabel
                                        key={group.groupUuid}
                                        control={
                                            <Checkbox
                                                checked={selectedGroups.includes(group.groupUuid)}
                                                onChange={() => handleGroupToggle(group.groupUuid)}
                                            />
                                        }
                                        label={group.groupName}
                                    />
                                ))
                            )}
                        </FormGroup>
                    </FormControl>

                    <Divider />

                    {/* Type Selection */}
                    <FormControl component="fieldset">
                        <FormLabel component="legend" sx={{ fontWeight: 'bold', marginBottom: 1 }}>
                            Disconnect Type
                        </FormLabel>
                        <RadioGroup
                            value={selectedType}
                            onChange={(e) => setSelectedType(e.target.value)}
                        >
                            <FormControlLabel value="both" control={<Radio />} label="Both (Remote and Non-Remote)" />
                            <FormControlLabel value="remote" control={<Radio />} label="Remote Only" />
                            <FormControlLabel value="nonRemote" control={<Radio />} label="Non-Remote Only" />
                        </RadioGroup>
                    </FormControl>

                    {/* Summary */}
                    <Box sx={{ 
                        padding: 2, 
                        backgroundColor: 'grey.100', 
                        borderRadius: 1,
                        marginTop: 1
                    }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', marginBottom: 0.5 }}>
                            Print Summary:
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {getSummaryText()}
                        </Typography>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handlePrint} variant="contained" startIcon={<PrintIcon />}>
                    Print
                </Button>
            </DialogActions>
        </Dialog>
    );
};

PrintDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onPrint: PropTypes.func.isRequired,
    availableGroups: PropTypes.array.isRequired,
    printScope: PropTypes.oneOf(['day', 'week']).isRequired,
    getFilteredGroupCount: PropTypes.func,
};

export default PrintDialog;

